import CustomListBox from 'components/CustomListBox';
import { joinPartnerDiscountCategories } from 'helpers/partner.helpers';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { DiscountCategories, DiscountCategoriesDefault } from 'types/Partner';

import {
  Autocomplete,
  Box,
  Checkbox as MuiCheckbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material';

const options = Object.entries(DiscountCategories);

type Props = {
  name: string;
  discountIndex: number;
};

export const CategoryInput: React.FC<Props> = ({ name, discountIndex }) => {
  const { control, trigger, setValue } = useFormContext();
  const values = useWatch({ control, name });
  const discountsUpdate = useWatch({ control, name: 'discountsUpdate' });

  const inputValue = useMemo(
    () =>
      joinPartnerDiscountCategories({
        categories: values,
        showOthersTitle: true,
      }),
    [values]
  );

  const checkDisabled = (path: string) => {
    return _.some(
      discountsUpdate,
      (item: any, index: number) =>
        index !== discountIndex && _.get(item, `categories.${path}.isSelected`)
    );
  };

  return (
    <Controller
      name={name}
      render={({ fieldState: { error } }) => {
        return (
          <FormControl error={Boolean(error)} fullWidth>
            <Autocomplete
              ListboxComponent={CustomListBox}
              disableCloseOnSelect
              id="grouped-categories"
              options={options}
              // value={values}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Selecionar itens"
                  required
                  error={Boolean(error)}
                />
              )}
              onClose={() => {
                trigger(name);
              }}
              getOptionLabel={([title]) => title}
              fullWidth
              inputValue={inputValue}
              filterOptions={(option) => option}
              filterSelectedOptions={false}
              renderOption={(
                __,
                // @ts-ignore
                [category, { title: categoryTitle, subcategories }]
              ) => {
                const fieldName = `${name}.${category}`;
                const allDisabled =
                  subcategories &&
                  _.keys(subcategories).every((subcategoryName) =>
                    checkDisabled(
                      `${category}.subcategories.${subcategoryName}`
                    )
                  );

                const disabled =
                  category !== 'others' &&
                  (checkDisabled(category) || allDisabled);

                const unavailableSubcategories = _.keys(subcategories).filter(
                  (subcategoryName) =>
                    checkDisabled(
                      `${category}.subcategories.${subcategoryName}`
                    )
                );

                return (
                  <Option
                    key={category}
                    category={category}
                    disabled={disabled}
                    categoryTitle={categoryTitle}
                    subcategories={subcategories}
                    fieldName={fieldName}
                    unavailableSubcategories={unavailableSubcategories}
                  >
                    {_.toPairs(subcategories).map(
                      // @ts-ignore
                      ([subcategory, { title }]) => {
                        const subcategoryFieldName = `${fieldName}.subcategories.${subcategory}`;

                        const handleAddTitle = () => {
                          setValue(`${fieldName}.title`, categoryTitle);
                        };

                        return (
                          <SubCategory
                            key={subcategoryFieldName}
                            fieldName={subcategoryFieldName}
                            subcategory={subcategory}
                            label={title}
                            category={category}
                            onAddTitle={handleAddTitle}
                            disabled={checkDisabled(
                              `${category}.subcategories.${subcategory}`
                            )}
                          />
                        );
                      }
                    )}
                  </Option>
                );
              }}
            />

            <FormHelperText>{error?.message}</FormHelperText>
          </FormControl>
        );
      }}
    />
  );
};

const Option: React.FC<{
  fieldName: string;
  category: string;
  categoryTitle: string;
  disabled: boolean;
  subcategories: any; // TODO: Add corrent types
  unavailableSubcategories: string[];
}> = ({
  category,
  subcategories,
  categoryTitle,
  fieldName,
  disabled,
  children,
  unavailableSubcategories,
}) => {
  const { setValue, watch } = useFormContext();
  const values = watch(fieldName);
  const arrayValues = _.values(values?.subcategories);

  // Check if all subcategories is selected
  const allChecked =
    values?.isSelected ||
    (arrayValues.length > 0 && _.every(arrayValues, 'isSelected'));
  // Check if some subcategories is selected
  const indeterminate = !allChecked && _.some(arrayValues, 'isSelected');

  const handleChange = () => {
    if (!subcategories) {
      return setValue(
        fieldName,
        // @ts-ignore
        { ...DiscountCategories[category], isSelected: !values?.isSelected }
      );
    }

    if (allChecked || indeterminate) {
      // @ts-ignore
      setValue(fieldName, DiscountCategoriesDefault[category]);
    } else {
      // @ts-ignore
      const selectedCategory = DiscountCategories[category];

      setValue(fieldName, {
        title: selectedCategory?.title,
        subcategories: {
          // @ts-ignore
          ...DiscountCategoriesDefault[category]?.subcategories,
          ..._.omit(selectedCategory?.subcategories, unavailableSubcategories),
        },
      });
    }
  };

  return (
    <Box key={category} ml={3}>
      <FormControl component="fieldset" required={true} disabled={disabled}>
        <FormGroup>
          <FormControlLabel
            name={fieldName}
            label={
              <Typography color={disabled ? 'GrayText' : 'black'}>
                {categoryTitle}
                {disabled && (
                  <Typography component="span" fontSize={12}>
                    {` (Já está em uso)`}
                  </Typography>
                )}
              </Typography>
            }
            checked={allChecked}
            value={allChecked}
            onClick={(e) => {
              if (disabled) {
                return;
              }

              e.preventDefault();
              handleChange();
            }}
            control={
              <MuiCheckbox name={fieldName} indeterminate={indeterminate} />
            }
          />

          <Box display="flex" flexDirection="column" ml={2}>
            {children}
          </Box>
        </FormGroup>
      </FormControl>
    </Box>
  );
};

const SubCategory: React.FC<{
  fieldName: string;
  category: string;
  subcategory: string;
  label: string;
  onAddTitle: () => void;
  disabled: boolean;
}> = ({ fieldName, label, subcategory, onAddTitle, disabled, category }) => {
  const { watch, setValue } = useFormContext();
  const value = watch(fieldName)?.isSelected ?? false;

  return (
    <FormControlLabel
      label={
        <Typography color={disabled ? 'GrayText' : 'black'}>
          {label}
          {disabled && (
            <Typography component="span" fontSize={12}>
              {` (Já está em uso)`}
            </Typography>
          )}
        </Typography>
      }
      id={fieldName}
      name={fieldName}
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault();

        if (disabled) {
          return;
        }

        setValue(fieldName, {
          isSelected: !value,
          title: label,
        });

        onAddTitle();
      }}
      checked={value}
      value={subcategory}
      control={<MuiCheckbox />}
    />
  );
};
